import {
  PlacementWishSchema,
  SeatReferenceSchema,
} from "@gen/travel/v1/cart_pb";
import { PlacementCapability } from "@gen/travel/v1/search_pb";
import { useEffect, useState } from "react";
import { create, JsonObject } from "@bufbuild/protobuf";
import { useBookingStore } from "@stores/useBookingStore";
import { t } from "i18next";
import { Button } from "@thetreep/ui";

type PlacementSeatMapProps = {
  transportNumber: string | undefined;
  passengerNumber: number;
  placementCapability: PlacementCapability[];
  segmentId: string;
};

export default function PlacementSeatMap({
  transportNumber,
  passengerNumber,
  placementCapability,
  segmentId,
}: PlacementSeatMapProps) {
  const { addPlacementWish } = useBookingStore();

  const [showInput, setShowInput] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [coachValue, setCoachValue] = useState<string>();
  const [seatNumberValue, setSeatNumberValue] = useState<string>();

  const handleEdit = () => {
    setShowInput(true);
    setEditMode(false);
  };

  function launchSeatMap(
    dataPlacement: JsonObject[],
    forceVerticalDisplay: boolean,
    numberOfPassengers: number,
  ) {
    const setampID = `seatmap-${transportNumber}`;
    const seatmapElement = document.getElementById(setampID);

    if (!seatmapElement) {
      console.error(`Element with id ${setampID} not found.`);
      return;
    }
    // Check if a pao-seatmap-ui already exists
    if (seatmapElement.querySelector("pao-seatmap-ui")) {
      return;
    }

    const paoSeatmapUi = document.createElement(
      "pao-seatmap-ui",
    ) as HTMLElement & {
      numberOfPassengers?: number;
      data?: JsonObject;
      forceVerticalDisplay?: boolean;
    };

    seatmapElement.appendChild(paoSeatmapUi);
    paoSeatmapUi.numberOfPassengers = numberOfPassengers;
    paoSeatmapUi.data = dataPlacement[0];
    paoSeatmapUi.forceVerticalDisplay = forceVerticalDisplay;

    paoSeatmapUi.addEventListener("pao-seatmap-ui-selected", (e: Event) =>
      onSeatValidation(e),
    );
    paoSeatmapUi.addEventListener("pao-seatmap-ui-canceled", onSeatMapClose);

    type SeatMapSelectedDetail = {
      seatNumber: string;
    };

    type Detail = {
      coach: string;
      placings: [
        {
          seatNumber: string;
        },
      ];
    };

    function onSeatValidation(e: Event) {
      const customEvent = e as CustomEvent<Detail>;
      const detail = customEvent.detail;
      if (!detail) return;

      setCoachValue(detail.coach);
      setSeatNumberValue(
        detail.placings
          .map((placing: SeatMapSelectedDetail) => placing.seatNumber)
          .join(", "),
      );

      const place = create(SeatReferenceSchema, {
        coach: detail.coach.toString(),
        seatNumber: detail.placings
          .map((placing: SeatMapSelectedDetail) => placing.seatNumber)
          .join(", "),
      });

      addPlacementWish(
        create(PlacementWishSchema, {
          segmentId: segmentId,
          wish: {
            case: "seatMap",
            value: place,
          },
        }),
      );
      setEditMode(true);
      setShowInput(false);
    }
    function onSeatMapClose() {
      paoSeatmapUi.remove();
    }
  }

  useEffect(() => {
    if (
      placementCapability.some(
        (placement) => placement.capability.case === "seatMap",
      )
    ) {
      launchSeatMap(
        placementCapability
          .map((placement) =>
            placement.capability.case === "seatMap"
              ? placement.capability.value
              : undefined,
          )
          .filter((value): value is JsonObject => value !== undefined),
        true,
        passengerNumber,
      );
    }
  }, [editMode]);

  return (
    <>
      {showInput && (
        <div
          id={`seatmap-${transportNumber}`}
          className="extended-width h-[428px]"
        ></div>
      )}
      {editMode && (
        <div className="flex flex-col items-center md:flex-row justify-between">
          {coachValue && (
            <p className="text-gray-500 font-roboto text-md">
              <strong> {t("placement.carNumber")}: </strong> {coachValue}{" "}
            </p>
          )}
          {seatNumberValue && (
            <p className="text-gray-500 font-roboto text-md">
              {" "}
              <strong> {t("placement.placeNumber")}: </strong>{" "}
              {seatNumberValue}{" "}
            </p>
          )}
          <Button onClick={handleEdit}>{t("placement.edit")}</Button>
        </div>
      )}
    </>
  );
}
